/** HSK 3.x has seven curriculum grades: 1–6, then 7 for the combined 7–9 band. */
export const HSK_CURRICULUM_GRADE_COUNT = 7;

/** TBCL has seven curriculum grades; grades 8–10 are the Taiwan MOE chart milestones after it. */
export const TBCL_CURRICULUM_GRADE_COUNT = 7;

/**
 * A Browse → Levels material tab: a band of curriculum grades, or one even part of a single grade
 * (`part`, split by level index) when that grade is too big for one pack.
 */
export interface CurriculumMaterialBand {
  readonly description: string;
  readonly grades: readonly number[];
  /** Stable tab id, not a raw `curriculumGrade` number. */
  readonly id: number;
  readonly label: string;
  /** This band is part `index` (0-based) of `count` even parts of its single grade. */
  readonly part?: { readonly count: number; readonly index: number };
  /** The exam levels or chart the band covers ("HSK 2–4", "MOE B, part 1"). */
  readonly scope: string;
}

/** The Browse → Levels material-tab value of the Ready pack (curriculum grade 0), in both scripts. */
export const READY_MATERIAL_TAB_VALUE = 0;

const READY_MATERIAL_BAND: CurriculumMaterialBand = {
  description:
    "This level pack will teach you the fundamentals of Chinese Characters: the components that build them up, their pronunciations and how to read Pinyin, and how they combine to form words. By finishing this pack, you'll be **ready** to learn any Chinese character, word, or sentence that comes your way!",
  grades: [0],
  id: READY_MATERIAL_TAB_VALUE,
  label: "Ready",
  scope: "Fundamentals",
};

/**
 * Browse → Levels material tabs for the simplified (HSK + PRC tier) curriculum.
 * Traveler/Urbanite merge grades, and Xplorer/Yonder split Tier 2 (grade 9) in half by level index.
 */
export const HSK_MATERIAL_BANDS: readonly CurriculumMaterialBand[] = [
  READY_MATERIAL_BAND,
  {
    description:
      "Your first HSK characters: numbers, family, time, food and the everyday verbs that show up in almost every sentence.",
    grades: [1],
    id: 1,
    label: "Starter",
    scope: "HSK 1",
  },
  {
    description:
      "Enough to get around China on your own: ordering food, shopping, transport, directions, and chatting with the people you meet.",
    grades: [2, 3, 4],
    id: 2,
    label: "Traveler",
    scope: "HSK 2–4",
  },
  {
    description:
      "For living and working in a Chinese city: work, news, opinions and the signs, menus and messages of daily life.",
    grades: [5, 6],
    id: 3,
    label: "Urbanite",
    scope: "HSK 5–6",
  },
  {
    description:
      "The advanced HSK band: formal and academic writing, literature, and professional Chinese.",
    grades: [7],
    id: 4,
    label: "Veteran",
    scope: "HSK 7–9",
  },
  {
    description:
      "The rest of China’s 3,500 standard common characters (通用规范汉字表 Tier 1) that no HSK level teaches, for full everyday literacy.",
    grades: [8],
    id: 5,
    label: "Wordsmith",
    scope: "Tier 1",
  },
  {
    description:
      "The first half of the standard table’s Tier 2: less common characters met in names, places, specialist fields and classical texts.",
    grades: [9],
    id: 6,
    label: "Xplorer",
    part: { count: 2, index: 0 },
    scope: "Tier 2, part 1",
  },
  {
    description:
      "The second half of Tier 2, completing all 6,500 characters of the standard table’s first two tiers.",
    grades: [9],
    id: 7,
    label: "Yonder",
    part: { count: 2, index: 1 },
    scope: "Tier 2, part 2",
  },
  {
    description:
      "Tier 3: rare characters kept for surnames, place names, scientific terms and classical Chinese.",
    grades: [10],
    id: 8,
    label: "Zenith",
    scope: "Tier 3",
  },
];

/**
 * Browse → Levels material tabs for the traditional (TBCL + Taiwan MOE chart) curriculum. The TBCL
 * packs mirror the HSK ones; past TBCL 7 the packs follow the MOE charts: the rest of the 常用字
 * (A), then the 次常用字 (B) and the 罕用字 (C) in thirds by level index.
 */
export const TBCL_MATERIAL_BANDS: readonly CurriculumMaterialBand[] = [
  READY_MATERIAL_BAND,
  {
    description:
      "Your first TBCL characters: numbers, family, time and the everyday verbs that show up in almost every sentence.",
    grades: [1],
    id: 1,
    label: "Starter",
    scope: "TBCL 1",
  },
  {
    description:
      "Enough to get around Taiwan on your own: food, shopping, school, transport, and chatting with the people you meet.",
    grades: [2, 3, 4],
    id: 2,
    label: "Traveler",
    scope: "TBCL 2–4",
  },
  {
    description:
      "For living and working in a Taiwanese city: work, news, opinions and the signs, menus and messages of daily life.",
    grades: [5, 6],
    id: 3,
    label: "Urbanite",
    scope: "TBCL 5–6",
  },
  {
    description:
      "The final TBCL band: literature, academic writing and professional Chinese, for near-native reading.",
    grades: [7],
    id: 4,
    label: "Veteran",
    scope: "TBCL 7",
  },
  {
    description:
      "The rest of the Ministry of Education’s 4,808 common characters (常用字, chart A) that no TBCL level teaches, for full everyday literacy.",
    grades: [8],
    id: 5,
    label: "Wordsmith",
    scope: "MOE A",
  },
  {
    description:
      "The first third of the less-common characters (次常用字, chart B), met in names, places, specialist fields and classical texts.",
    grades: [9],
    id: 6,
    label: "Xplorer",
    part: { count: 3, index: 0 },
    scope: "MOE B, part 1",
  },
  {
    description: "The second third of the less-common characters (次常用字, chart B).",
    grades: [9],
    id: 7,
    label: "Yonder",
    part: { count: 3, index: 1 },
    scope: "MOE B, part 2",
  },
  {
    description:
      "The last third of the less-common characters (次常用字, chart B), completing the 11,142 characters of charts A and B.",
    grades: [9],
    id: 8,
    label: "Zenith",
    part: { count: 3, index: 2 },
    scope: "MOE B, part 3",
  },
  {
    description:
      "The first third of the rarely used characters (罕用字, chart C): surnames, place names, classical texts and historical variants.",
    grades: [10],
    id: 9,
    label: "Archivist",
    part: { count: 3, index: 0 },
    scope: "MOE C, part 1",
  },
  {
    description: "The second third of the rarely used characters (罕用字, chart C).",
    grades: [10],
    id: 10,
    label: "Bibliomaniac",
    part: { count: 3, index: 1 },
    scope: "MOE C, part 2",
  },
  {
    description:
      "The last third of the rarely used characters (罕用字, chart C), completing every character of the Ministry of Education’s three charts.",
    grades: [10],
    id: 11,
    label: "Completionist",
    part: { count: 3, index: 2 },
    scope: "MOE C, part 3",
  },
];

export type CurriculumDataset = "hsk" | "tbcl";

export function getCurriculumMaterialBands(
  dataset: CurriculumDataset,
): readonly CurriculumMaterialBand[] {
  return dataset === "tbcl" ? TBCL_MATERIAL_BANDS : HSK_MATERIAL_BANDS;
}

/** The Taiwan MOE chart milestones after TBCL 7 (grades 8–10). */
const TBCL_MOE_CHART_LABELS = ["MOE A", "MOE B", "MOE C"] as const;

/**
 * Display label for a character curriculum grade. Grades beyond the exam levels themselves are the
 * PRC 通用规范汉字表 tier milestones (HSK) or the Taiwan MOE chart milestones (TBCL): the tier's or
 * chart's characters that no earlier grade teaches.
 */
export function formatCurriculumGradeLabel(
  grade: number,
  {
    dataset,
    hskGradeCount = HSK_CURRICULUM_GRADE_COUNT,
  }: {
    dataset: CurriculumDataset;
    /** HSK 2.0 has six grades; HSK 3.x has seven. */
    hskGradeCount?: number;
  },
): string {
  if (grade === 0) {
    return "Ready";
  }
  if (dataset === "tbcl") {
    return grade > TBCL_CURRICULUM_GRADE_COUNT
      ? (TBCL_MOE_CHART_LABELS[grade - TBCL_CURRICULUM_GRADE_COUNT - 1] ?? `TBCL ${grade}`)
      : `TBCL ${grade}`;
  }
  if (grade > hskGradeCount) {
    return `Tier ${grade - hskGradeCount}`;
  }
  return grade === 7 ? "HSK 7–9" : `HSK ${grade}`;
}

/**
 * For each grade the dataset's bands split into parts, the first level `index` of every part, in
 * order (`[start of part 1, start of part 2, …]`). The grade's levels are divided evenly by count.
 */
export type CurriculumGradeParts = ReadonlyMap<number, readonly number[]>;

export function getCurriculumGradeParts(
  levels: readonly { curriculumGrade?: number; index: number }[],
  { dataset }: { dataset: CurriculumDataset },
): CurriculumGradeParts {
  const partCounts = new Map<number, number>();
  for (const band of getCurriculumMaterialBands(dataset)) {
    const grade = band.grades[0];
    if (grade !== undefined && band.part !== undefined) {
      partCounts.set(grade, band.part.count);
    }
  }
  const parts = new Map<number, readonly number[]>();
  for (const [grade, count] of partCounts) {
    const gradeIndexes = levels
      .filter((level) => (level.curriculumGrade ?? 1) === grade)
      .map((level) => level.index)
      .sort((a, b) => a - b);
    if (gradeIndexes.length === 0) {
      continue;
    }
    parts.set(
      grade,
      Array.from(
        { length: count },
        (_, part) => gradeIndexes[Math.floor((part * gradeIndexes.length) / count)] ?? Infinity,
      ),
    );
  }
  return parts;
}

/** Which even part of its grade a level falls in (0 when the grade isn't split). */
function curriculumGradePartOf(
  grade: number,
  levelIndex: number,
  gradeParts: CurriculumGradeParts,
): number {
  const starts = gradeParts.get(grade);
  if (starts === undefined) {
    return 0;
  }
  return Math.max(0, starts.filter((start) => start <= levelIndex).length - 1);
}

function materialBandContainsLevel(
  band: CurriculumMaterialBand,
  grade: number,
  levelIndex: number,
  gradeParts: CurriculumGradeParts,
): boolean {
  if (!band.grades.includes(grade)) {
    return false;
  }
  return (
    band.part === undefined ||
    band.part.index === curriculumGradePartOf(grade, levelIndex, gradeParts)
  );
}

/** The Browse → Levels material-tab value (band id) for a level. */
export function getCurriculumMaterialTabValue(
  level: { curriculumGrade?: number; index: number },
  {
    dataset,
    gradeParts,
  }: {
    dataset: CurriculumDataset;
    gradeParts: CurriculumGradeParts;
  },
): number {
  const grade = level.curriculumGrade ?? 1;
  for (const band of getCurriculumMaterialBands(dataset)) {
    if (materialBandContainsLevel(band, grade, level.index, gradeParts)) {
      return band.id;
    }
  }
  return grade;
}

/** Whether a level belongs under the selected Browse → Levels material tab. */
export function levelMatchesCurriculumMaterialTab(
  level: { curriculumGrade?: number; index: number },
  tabValue: number,
  options: {
    dataset: CurriculumDataset;
    gradeParts: CurriculumGradeParts;
  },
): boolean {
  return getCurriculumMaterialTabValue(level, options) === tabValue;
}

/**
 * What a Browse → Levels material tab teaches, shown above its first level: `scope` names the exam
 * levels or chart it covers ("HSK 2–4", "MOE A"), `description` says what those characters let the
 * learner do.
 */
export function getCurriculumMaterialTabDescription(
  tabValue: number,
  { dataset }: { dataset: CurriculumDataset },
): { description: string; scope: string | null; title: string } | null {
  const band = getCurriculumMaterialBands(dataset).find((candidate) => candidate.id === tabValue);
  return band === undefined
    ? null
    : { description: band.description, scope: band.scope, title: `${band.label} Pack` };
}

/**
 * Browse → Levels material tabs, left to right: Starter → Zenith (simplified) or
 * Starter → Completionist (traditional). A band with no levels has no tab.
 */
export function buildCurriculumMaterialTabOptions(
  levels: readonly { curriculumGrade?: number; index: number }[],
  { dataset }: { dataset: CurriculumDataset },
): { label: string; value: number }[] {
  const gradeParts = getCurriculumGradeParts(levels, { dataset });
  return getCurriculumMaterialBands(dataset)
    .filter((band) =>
      levels.some((level) =>
        levelMatchesCurriculumMaterialTab(level, band.id, { dataset, gradeParts }),
      ),
    )
    .map((band) => ({ label: band.label, value: band.id }));
}
